'use client'

import type { RowPinningPosition } from '@tanstack/react-table'
import type { MouseEvent } from 'react'
import type {
	ButtonProps,
	SRT_Row,
	SRT_RowData,
	SRT_TableInstance,
} from '../../types'

import { useState } from 'react'

import { Button } from '@/shared/components/ui/button'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'

import { cn } from '../../lib/utils'

/* eslint-disable  @typescript-eslint/no-unused-vars */
export type SRT_RowPinButtonProps<TData extends SRT_RowData> = {
	pinningPosition: RowPinningPosition
	row: SRT_Row<TData>
	table: SRT_TableInstance<TData>
} & ButtonProps

export const SRT_RowPinButton = <TData extends SRT_RowData>({
	pinningPosition,
	row,
	table,
	...rest
}: SRT_RowPinButtonProps<TData>) => {
	const {
		options: {
			icons: { CloseIcon, PushPinIcon },
			localization,
			rowPinningDisplayMode,
		},
	} = table

	const isPinned = row.getIsPinned()
	const [tooltipOpened, setTooltipOpened] = useState(false)

	const handleTogglePin = (event: MouseEvent<HTMLButtonElement>) => {
		setTooltipOpened(false)
		event.stopPropagation()
		row.pin(isPinned ? false : pinningPosition)
	}

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<Button
					aria-label={localization.pin}
					size='icon-sm'
					variant='ghost'
					className={cn(
						'h-6 w-6',
						rest.className //Pass other className
					)}
					{...rest}
				>
					{isPinned ? (
						<CloseIcon />
					) : (
						<PushPinIcon
							className={cn(
								rowPinningDisplayMode === 'sticky'
									? 'rotate-[135deg]'
									: pinningPosition === 'top'
										? 'rotate-180'
										: 'rotate-0'
							)}
						/>
					)}
				</Button>
			</TooltipTrigger>
			<TooltipContent>
				{isPinned ? localization.unpin : localization.pin}
			</TooltipContent>
		</Tooltip>
	)
}
